import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import MaldivesWasPriceRow from './MaldivesWasPriceRow'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import type { FlightsClassTypes } from 'constants/flight'
// Hooks
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import type { CalendarItemBundleData } from '../hooks/types'
import useCalendarItemPricing from './useCalendarItemPricing'
import MaldivesFnfSpecialOffer from './MaldivesFnfSpecialOffer'

// Utils
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  prefetchedBundleData?: CalendarItemBundleData
}

const InventoryGroup = styled(Group)`
  width: ${rem(170)};
`

const InventoryLabelGroup = styled(Group)`
  min-width: ${rem(155)};
`

const SelectButton = styled(TextButton)`
  width: ${rem(90)};
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesDirectSearchFlightCalendarItem(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    prefetchedBundleData,
  } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { isKidsFlyFreeDate } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const fetchedBundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    disabled: !!prefetchedBundleData,
  })

  const bundleData = prefetchedBundleData ?? fetchedBundleData

  const {
    regularPrice,
    memberPrice,
    isMemberPrice,
    displayPrice,
    wasPrice,
    shouldShowWasPrice,
    inventoryMessageType,
    inventoryMessage,
    fnfInventoryMessage,
    specialOfferLabel,
  } = useCalendarItemPricing({
    bundleData,
    luxPlusEnabled,
    priceMode,
  })

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const selectText = intl.formatMessage({
    id: 'MvGwxY9AMe6ETPfczwZK',
    defaultMessage: 'Select',
    description: 'Button label to select a charter flight date in the flights flow',
  })
  const unavailableText = intl.formatMessage({
    id: 'MkdIPUtrVEQzhLPF4i4b',
    defaultMessage: 'Unavailable',
    description: 'Button label when charter flight date is unavailable',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const nightsAndFlightsText = intl.formatMessage(
    {
      id: 'r484bkJ_LnqWqW8S-s9D',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Label showing number of nights and flights included in charter bundle',
    },
    { nights },
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5TknjYVpdoEi',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })

  // Render select button based on state
  const selectButton = isSelected ? (
    <SelectButton
      kind="primary"
      nonInteractable
      tabIndex={-1}
    >
      {selectedText}
    </SelectButton>
  ) : (
    <SelectButton
      kind="secondary"
      onClick={() => onSelect(option)}
      disabled={!bundleData.available}
    >
      {bundleData.available ? selectText : unavailableText}
    </SelectButton>
  )

  // Render price display based on availability
  let priceDisplay
  if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        {loadingText}
      </BodyText>
    )
  } else if (displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <div>
          <FormattedMessage
            id="n4PanjTCe-LnLjdxEfIQ"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives flight calendar"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'neutral-two'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
        {shouldShowWasPrice && wasPrice != null && <MaldivesWasPriceRow
          altPrice={wasPrice}
          saleUnit={priceMode === 'perPerson' ? '/person' : undefined}
        />}
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {unavailableText}
      </BodyText>
    )
  }

  return (
    <>
      {/* Column 1: Departure date */}
      <div>
        <BodyText variant="small" colour="neutral-three">
          {dateFormatter(departDate, INTL_LONG_WEEKDAY)}
        </BodyText>
        <Heading variant="heading4" wrap="no-wrap">
          {dateFormatter(departDate, INTL_DMY_CASUAL_FORMAT)}
        </Heading>
      </div>

      {/* Column 2: Nights */}
      <Group direction="vertical" gap={4} horizontalAlign="center">
        <LineArrowRightIcon size="M" />
        <BodyText variant="small" colour="neutral-three">
          {nightsAndFlightsText}
        </BodyText>
      </Group>

      {/* Column 3: Return date */}
      <div>
        <BodyText variant="small" colour="neutral-three">
          {dateFormatter(checkOut, INTL_LONG_WEEKDAY)}
        </BodyText>
        <Heading variant="heading4" wrap="no-wrap">
          {dateFormatter(checkOut, INTL_DMY_CASUAL_FORMAT)}<SmallSup>{dayOffsetText}</SmallSup>
        </Heading>
      </div>

      {/* Column 5: Kids Fly Free + Availability Warning */}
      {(() => {
        return (
          <Group direction="vertical" horizontalAlign="center" gap={0}>
            <InventoryLabelGroup direction="vertical" horizontalAlign="start" verticalAlign="center" gap={2}>
              {(!bundleData.isFnfPriceApplied || inventoryMessageType !== 'seat') && inventoryMessage !== '' && <InventoryGroup direction="horizontal" verticalAlign="center" gap={4}>
                <SolidExclamationCircleIcon colour="urgency" size="XS" />
                <BodyText variant="small" colour="critical">
                  {inventoryMessage}
                </BodyText>
              </InventoryGroup>}
              {bundleData.isFnfPriceApplied && (
                <InventoryGroup direction="vertical" horizontalAlign="center" verticalAlign="center" gap={4}>
                  <MaldivesFnfSpecialOffer
                    specialOfferLabel={specialOfferLabel}
                    fnfInventoryMessage={fnfInventoryMessage}
                  />
                </InventoryGroup>
              )}
              {isKidsFlyFreeDate(departDate) && <KidsFlyFreeLabel variant="short" />}
            </InventoryLabelGroup>
          </Group>
        )
      })()}

      {/* Column 6: Price and Select Button */}
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
        <VerticalSpacer gap={4}>
          {priceDisplay}
        </VerticalSpacer>
        {selectButton}
      </Group>
    </>
  )
}

export default MaldivesDirectSearchFlightCalendarItem
